import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { FleetMockStore } from '../../mock/fleet-mock.store';
import { FleetVehicle } from '../../mock/models/fleet.models';

/**
 * VehicleAnalyticsPanelComponent — segundo nivel del dashboard: analíticas
 * de UN vehículo. Recibe `vehicleId` y se suscribe a `store.vehicle$(id)`.
 * Maneja el caso de vehículo inexistente sin romper la vista.
 */
@Component({
  selector: 'app-vehicle-analytics-panel',
  templateUrl: './vehicle-analytics-panel.component.html',
  styleUrls: ['./vehicle-analytics-panel.component.css'],
})
export class VehicleAnalyticsPanelComponent implements OnChanges, OnDestroy {
  @Input() vehicleId: string | null = null;

  vehicle?: FleetVehicle;

  private readonly monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  // ---- Opciones ApexCharts (tipado laxo). ----
  gauge: any;
  radar: any;
  scoreTrend: any;
  fuelBars: any;

  private sub?: Subscription;

  constructor(private store: FleetMockStore) {
    // do nothing
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vehicleId']) {
      this.sub?.unsubscribe();
      if (!this.vehicleId) {
        this.vehicle = undefined;
        return;
      }
      this.sub = this.store.vehicle$(this.vehicleId).subscribe((v) => {
        this.vehicle = v;
        if (v) {
          this.buildCharts(v);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private buildCharts(v: FleetVehicle): void {
    const a = v.analytics;

    // GAUGE — driverScore 0-100.
    const scoreColor = a.driverScore >= 80 ? '#10b981' : a.driverScore >= 60 ? '#f59e0b' : '#ef4444';
    this.gauge = {
      series: [a.driverScore],
      chart: { type: 'radialBar', height: 240, fontFamily: 'inherit', foreColor: '#888ea8' },
      colors: [scoreColor],
      plotOptions: {
        radialBar: {
          hollow: { size: '58%' },
          track: { background: 'rgba(136,142,168,0.15)' },
          dataLabels: {
            name: { show: true, offsetY: 22, color: '#888ea8', fontSize: '13px' },
            value: { show: true, offsetY: -12, fontSize: '30px', fontWeight: 700, color: scoreColor, formatter: (val: number) => `${Math.round(val)}` },
          },
        },
      },
      fill: { type: 'solid' },
      stroke: { lineCap: 'round' },
      labels: ['Puntaje'],
    };

    // RADAR — comportamiento de conducción.
    this.radar = {
      series: [{ name: 'Comportamiento', data: [a.behavior.braking, a.behavior.acceleration, a.behavior.cornering, a.behavior.speeding] }],
      chart: { type: 'radar', height: 300, fontFamily: 'inherit', foreColor: '#888ea8', toolbar: { show: false } },
      colors: ['#805dca'],
      xaxis: { categories: ['Frenado', 'Aceleración', 'Curvas', 'Vel. excesiva'] },
      yaxis: { show: false, max: 100 },
      fill: { opacity: 0.25 },
      stroke: { width: 2 },
      markers: { size: 4 },
      plotOptions: { radar: { polygons: { strokeColors: 'rgba(136,142,168,0.2)', connectorColors: 'rgba(136,142,168,0.2)' } } },
      tooltip: { theme: 'dark' },
    };

    // LÍNEA — evolución del puntaje (12 meses).
    this.scoreTrend = {
      series: [{ name: 'Puntaje', data: a.driverScoreSeries }],
      chart: { type: 'line', height: 260, fontFamily: 'inherit', foreColor: '#888ea8', toolbar: { show: false }, zoom: { enabled: false } },
      colors: ['#4361ee'],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 3 },
      markers: { size: 0, hover: { size: 5 } },
      xaxis: { categories: this.monthLabels, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { min: 0, max: 100 },
      grid: { borderColor: 'rgba(136,142,168,0.15)' },
      tooltip: { theme: 'dark' },
    };

    // BARRAS — costo mensual de combustible (COP).
    this.fuelBars = {
      series: [{ name: 'Combustible (COP)', data: a.monthlyFuelCost.map((m) => Math.round(m * 1000)) }],
      chart: { type: 'bar', height: 260, fontFamily: 'inherit', foreColor: '#888ea8', toolbar: { show: false } },
      colors: ['#e2a03f'],
      dataLabels: { enabled: false },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
      xaxis: { categories: this.monthLabels, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { formatter: (val: number) => `$${(val / 1000000).toFixed(1)}M` } },
      grid: { borderColor: 'rgba(136,142,168,0.15)' },
      tooltip: { theme: 'dark', y: { formatter: (val: number) => `$${val.toLocaleString('es-CO')}` } },
    };
  }
}
